'use es6';

import { Fragment, useCallback, useMemo } from 'react';
import Raven from 'raven-js';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { FETCH_UNIVERSAL_SIDEBAR } from 'customer-data-sidebar/universal/queries';
import { VISIT } from 'customer-data-objects/constants/ObjectTypes';
import UniversalSidebarCards from './UniversalSidebarCards';
import UniversalSidebarLoading from 'customer-data-sidebar/cards/UniversalSidebarLoading';
import ContentLoading from 'customer-data-sidebar/cards/ContentLoading';
import { PermissionsContext } from './context/PermissionsContext';
import { useCardLocationContext } from './context/CardLocationContext';
import { CardStateManipulationContext } from './context/CardStateManipulationContext';
import { useUpdateCardOpen } from './hooks/useUpdateCardOpen';
import { useUpdateCardOrder } from './hooks/useUpdateCardOrder';
import { QueryContext } from './context/QueryContext';
import { CRM_RECORD_LEFT, CRM_RECORD_RIGHT, CONVERSATIONS_INBOX, HELPDESK } from 'customer-data-sidebar/universal/cardLocationConstants';
import UniversalSidebarHighlightWrapper from './UniversalSidebarHighlightWrapper';
import { SidebarSubjectContext } from './context/SidebarSubjectContext';
import UniversalSidebarPanelCardsWrapper from './UniversalSidebarPanelCardsWrapper';
import UniversalSidebarFooter from './UniversalSidebarFooter';
import { AnyCrmObjectTypePropType } from 'customer-data-objects-ui-components/propTypes/CrmObjectTypes';
import getCrmObjectPrimaryDisplayLabel from 'customer-data-sidebar/universal/dataAccessHelpers/getCrmObjectPrimaryDisplayLabel';
import UniversalSidebarError from './errors/UniversalSidebarError';
import { getUserCanEdit, getUserCanView, getUserCanDelete } from './permissions/UserPermissions';
import { INLINE, INLINE_NO_FOOTER, PANEL, UniversalSidebarUsePropType } from 'customer-data-sidebar/universal/constants/UniversalSidebarUseConstants';
import { EditableAreasContext } from './context/EditableAreasContext';
import { previewEditableAreas, universalEditableAreas } from 'customer-data-properties/savebar/EditableAreas';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import { useOnCrmMessageTopic } from 'crm-message-bus/useOnCrmMessageTopic';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ALWAYS_DND_ENABLED_CARD_LOCATIONS = [CRM_RECORD_LEFT, CONVERSATIONS_INBOX, HELPDESK];
const UniversalSidebarCardsContainer = ({
  auth,
  cardComponents,
  cardProps,
  HighlightComponent,
  objectId,
  objectType,
  onClose,
  readOnly,
  RhumbMarkerSuccess,
  RhumbMarkerError,
  use
}) => {
  const authUser = auth.user;
  const isVisit = objectType === VISIT;
  const {
    cardLocation
  } = useCardLocationContext();
  const isOnRecordPage = [CRM_RECORD_LEFT, CRM_RECORD_RIGHT].includes(cardLocation);
  const variables = {
    cardLocation,
    objectType,
    subjectId: !isVisit ? parseInt(objectId, 10) : -1
  };
  const query = useQuery(FETCH_UNIVERSAL_SIDEBAR, {
    variables,
    partialRefetch: true,
    fetchPolicy: isOnRecordPage ? 'cache-first' : 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onError: error => {
      Raven.captureException(new Error('UniversalSidebarCardsContainer query error'), {
        extra: {
          error,
          data: query.data,
          variables,
          isOnRecordPage
        }
      });
    }
  });
  const {
    loading,
    error,
    data,
    refetch,
    updateQuery
  } = query;
  const cards = data && data.crmObject && data.crmObject.cards;
  const allProperties = data && data.crmObject && data.crmObject.allProperties;
  const handleCardUpdated = useCallback(payload => {
    const isLocationContainsCard = cards.some(card => card.cardId === payload.cardId);
    if (isLocationContainsCard) {
      refetch();
    }
  }, [cards, refetch]);
  useOnCrmMessageTopic(TOPIC_NAMES.UPDATE_CARD_CONFIGURATION, handleCardUpdated);
  const handleCardOpen = useUpdateCardOpen({
    objectType
  });
  const handleOrderChange = useUpdateCardOrder({
    objectType,
    updateQuery
  });
  const loadingOrError = useMemo(() => {
    if (error) {
      return /*#__PURE__*/_jsxs(Fragment, {
        children: [use === PANEL && /*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: onClose
        }), RhumbMarkerError, /*#__PURE__*/_jsx(UniversalSidebarError, {
          message: error.message
        })]
      });
    }
    if (loading && isOnRecordPage) {
      return /*#__PURE__*/_jsx(UniversalSidebarLoading, {
        cardLocation: cardLocation
      });
    }
    if (loading) {
      return /*#__PURE__*/_jsx(ContentLoading, {});
    }
    return null;
  }, [RhumbMarkerError, cardLocation, error, isOnRecordPage, loading, onClose, use]);
  const sidebarContent = useMemo(() => {
    if (loadingOrError || !data) return null;
    const primaryDisplayLabel = getCrmObjectPrimaryDisplayLabel(objectType, data.crmObject);
    const isCustomized = data.crmObject.crmRecordView.isCustomized;
    const allowDnd = cardLocation === CRM_RECORD_RIGHT && !isCustomized || ALWAYS_DND_ENABLED_CARD_LOCATIONS.includes(cardLocation);
    const editableAreas = use === PANEL ? previewEditableAreas : universalEditableAreas;
    return /*#__PURE__*/_jsx("div", {
      "data-sidebar-location": cardLocation,
      children: /*#__PURE__*/_jsx(SidebarSubjectContext.Provider, {
        value: {
          allProperties,
          definition: data.crmObject.objectTypeDefinition,
          objectType,
          objectId,
          primaryDisplayLabel,
          primaryDisplayProperty: data.crmObject.primaryDisplayProperty
        },
        children: /*#__PURE__*/_jsx(PermissionsContext.Provider, {
          value: {
            canEditSubject: getUserCanEdit(data.crmObject.userPermissions),
            canDeleteSubject: getUserCanDelete(data.crmObject.userPermissions),
            canView: getUserCanView(data.crmObject.userPermissions),
            scopes: authUser.scopes,
            readOnly,
            gates: auth.gates
          },
          children: /*#__PURE__*/_jsx(CardStateManipulationContext.Provider, {
            value: {
              onCardOpenChange: handleCardOpen,
              onCardOrderChange: handleOrderChange
            },
            children: /*#__PURE__*/_jsx(QueryContext.Provider, {
              value: query,
              children: /*#__PURE__*/_jsxs(EditableAreasContext.Provider, {
                value: editableAreas,
                children: [RhumbMarkerSuccess, /*#__PURE__*/_jsx(UniversalSidebarHighlightWrapper, {
                  auth: auth,
                  HighlightComponent: HighlightComponent,
                  objectType: objectType,
                  objectId: objectId
                }), /*#__PURE__*/_jsx(UniversalSidebarCards, {
                  cards: cards,
                  draggable: allowDnd,
                  objectId: objectId,
                  cardComponents: cardComponents,
                  objectType: objectType,
                  cardLocation: cardLocation,
                  cardProps: cardProps,
                  refetch: refetch
                })]
              })
            })
          })
        })
      })
    });
  }, [HighlightComponent, RhumbMarkerSuccess, allProperties, auth, authUser.scopes, cardComponents, cardLocation, cardProps, cards, data, handleCardOpen, handleOrderChange, loadingOrError, objectId, objectType, query, readOnly, refetch, use]);
  if (loadingOrError) {
    return loadingOrError;
  }
  if (use === PANEL) {
    return /*#__PURE__*/_jsx(UniversalSidebarPanelCardsWrapper, {
      label: getCrmObjectPrimaryDisplayLabel(objectType, data.crmObject),
      objectTypeId: objectType,
      objectId: objectId,
      onClose: onClose,
      readOnly: readOnly,
      children: sidebarContent
    });
  } else if (use === INLINE) {
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [sidebarContent, /*#__PURE__*/_jsx(UniversalSidebarFooter, {})]
    });
  } else if (use === INLINE_NO_FOOTER) {
    return /*#__PURE__*/_jsx(Fragment, {
      children: sidebarContent
    });
  } else {
    throw new Error(`[UniversalSidebar]: Unrecognized 'use' prop: ${use} (must be one of: 'inline', 'inline-no-footer', 'panel')`);
  }
};
UniversalSidebarCardsContainer.propTypes = {
  auth: PropTypes.shape({
    user: PropTypes.shape({
      scopes: PropTypes.arrayOf(PropTypes.string).isRequired,
      user_id: PropTypes.number.isRequired,
      teams: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired
      })).isRequired
    }).isRequired,
    gates: PropTypes.arrayOf(PropTypes.string).isRequired,
    portal: PropTypes.object
  }),
  cardComponents: PropTypes.object,
  cardProps: PropTypes.object,
  HighlightComponent: PropTypes.elementType,
  objectType: AnyCrmObjectTypePropType.isRequired,
  objectId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  use: UniversalSidebarUsePropType.isRequired,
  RhumbMarkerSuccess: PropTypes.node,
  RhumbMarkerError: PropTypes.node
};
export default UniversalSidebarCardsContainer;